// src/pages/AdminPage/index.tsx
// Контейнер админки: заголовок + внутренние вкладки.
// Объединяет реальный API-функционал (Пользователи, Роли, Журнал действий)
// и демо-вкладки пресейла (Модели, Политики компании, Импорт сценариев).
import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import UsersTab from './UsersTab';
import RolesTab from './RolesTab';
import AuditTab from './AuditTab';
import ModelsTab from './ModelsTab';
import PoliciesTab from './PoliciesTab';
import ScenariosImportTab from './ScenariosImportTab';
import SettingsTab from './SettingsTab';

type AdminTabId =
  | 'users'
  | 'roles'
  | 'audit'
  | 'models'
  | 'policies'
  | 'scenarios-import'
  | 'settings';

const ADMIN_TABS: Array<{ id: AdminTabId; label: string }> = [
  { id: 'users', label: 'Пользователи' },
  { id: 'roles', label: 'Роли' },
  { id: 'audit', label: 'Журнал действий' },
  { id: 'models', label: 'Модели' },
  { id: 'policies', label: 'Политики компании' },
  { id: 'scenarios-import', label: 'Импорт сценариев' },
  { id: 'settings', label: 'Настройки' },
];

export default function AdminPage() {
  const [tab, setTab] = useState<AdminTabId>('users');

  return (
    <Box sx={{ p: { xs: 2, md: '32px 48px' }, maxWidth: 1600, mx: 'auto', width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 3.5 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px', mb: 0.5 }}>
          Администрирование
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
          Управление пользователями, ролями, моделями, политиками компании и настройками
        </Typography>
      </Box>

      {/* Таб-бар */}
      <Box
        sx={{
          display: 'inline-flex',
          gap: '2px',
          background: 'rgba(20,116,255,0.06)',
          border: '1px solid rgba(20,116,255,0.12)',
          borderRadius: '9px',
          p: '3px',
          mb: 3,
        }}
      >
        {ADMIN_TABS.map((t) => {
          const isActive = tab === t.id;
          return (
            <Box
              key={t.id}
              onClick={() => setTab(t.id)}
              sx={{
                px: 1.75,
                py: '5px',
                borderRadius: '6px',
                fontSize: 12.5,
                fontWeight: 600,
                cursor: 'pointer',
                color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                background: isActive ? 'rgba(255,255,255,0.92)' : 'transparent',
                boxShadow: isActive ? '0 1px 4px rgba(20,116,255,0.14)' : 'none',
                transition: 'all 0.15s ease',
                userSelect: 'none',
                letterSpacing: '-0.01em',
                '&:hover': {
                  color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                  background: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.60)',
                },
              }}
            >
              {t.label}
            </Box>
          );
        })}
      </Box>

      {tab === 'users' && <UsersTab />}
      {tab === 'roles' && <RolesTab />}
      {tab === 'audit' && <AuditTab />}
      {tab === 'models' && <ModelsTab />}
      {tab === 'policies' && <PoliciesTab />}
      {tab === 'scenarios-import' && <ScenariosImportTab />}
      {tab === 'settings' && <SettingsTab />}
    </Box>
  );
}
